/**
 * Spec 036: folds a `chart.json` message array into a `ChartModel` once
 * (`replayChart`) and truncates a model to a scrub bar (`truncateChart`).
 * Pure. Unknown message types are skipped and never throw.
 */
import type {
  CandlePoint,
  ChartMessage,
  ChartModel,
  ChartSeries,
  SeriesSpec,
  ValuePoint,
} from './types'

function emptyModel(): ChartModel {
  return { panes: [], series: [], markers: {}, levels: {}, zones: {}, circles: {} }
}

function toSeries(spec: SeriesSpec): ChartSeries {
  return {
    id: spec.id,
    pane: spec.pane,
    type: spec.type,
    color: spec.color ?? null,
    title: spec.title ?? null,
    height: spec.height ?? 1,
    points: spec.points ? [...spec.points] : [],
  }
}

function addSeries(model: ChartModel, spec: SeriesSpec): void {
  const next = toSeries(spec)
  const at = model.series.findIndex((s) => s.id === next.id)
  if (at >= 0) model.series[at] = next
  else model.series.push(next)
  if (!model.panes.includes(next.pane)) model.panes.push(next.pane)
}

function dropEmptyPanes(model: ChartModel): void {
  model.panes = model.panes.filter((p) => model.series.some((s) => s.pane === p))
}

function dropSeriesOverlays(model: ChartModel, id: string): void {
  delete model.markers[id]
  delete model.levels[id]
  delete model.zones[id]
  delete model.circles[id]
}

/** Folds the messages, in order, into one model. Never throws on odd input. */
export function replayChart(messages: ChartMessage[]): ChartModel {
  let model = emptyModel()
  for (const raw of messages) {
    const msg: { type?: unknown } = raw
    switch (msg.type) {
      case 'spec': {
        const m = raw as Extract<ChartMessage, { type: 'spec' }>
        model = emptyModel()
        for (const spec of m.panes ?? []) addSeries(model, spec)
        break
      }
      case 'add_series': {
        const m = raw as Extract<ChartMessage, { type: 'add_series' }>
        if (m.series) addSeries(model, m.series)
        break
      }
      case 'append': {
        const m = raw as Extract<ChartMessage, { type: 'append' }>
        const target = model.series.find((s) => s.id === m.series)
        if (target) target.points = [...target.points, ...(m.points ?? [])]
        break
      }
      case 'remove_series': {
        const m = raw as Extract<ChartMessage, { type: 'remove_series' }>
        model.series = model.series.filter((s) => s.id !== m.series)
        dropSeriesOverlays(model, m.series)
        dropEmptyPanes(model)
        break
      }
      case 'remove_pane': {
        const m = raw as Extract<ChartMessage, { type: 'remove_pane' }>
        for (const s of model.series.filter((x) => x.pane === m.pane)) dropSeriesOverlays(model, s.id)
        model.series = model.series.filter((s) => s.pane !== m.pane)
        model.panes = model.panes.filter((p) => p !== m.pane)
        break
      }
      case 'markers': {
        const m = raw as Extract<ChartMessage, { type: 'markers' }>
        model.markers[m.series] = [...(m.markers ?? [])]
        break
      }
      case 'price_levels': {
        const m = raw as Extract<ChartMessage, { type: 'price_levels' }>
        model.levels[m.series] = [...(m.levels ?? [])]
        break
      }
      case 'zones': {
        const m = raw as Extract<ChartMessage, { type: 'zones' }>
        model.zones[m.series] = [...(m.zones ?? [])]
        break
      }
      case 'circles': {
        const m = raw as Extract<ChartMessage, { type: 'circles' }>
        model.circles[m.series] = [...(m.circles ?? [])]
        break
      }
      case 'clear':
        model = emptyModel()
        break
      default:
        break
    }
  }
  return model
}

/** The first candlestick series of a model, if any. */
export function candleSeries(model: ChartModel): ChartSeries | null {
  return model.series.find((s) => s.type === 'candlestick') ?? null
}

/**
 * The model as it stood at candle `k` (0-based index into the first
 * candlestick series): everything after that bar's time is dropped, zones and
 * circles straddling it are clipped to it, and a level with `time_end: null`
 * keeps `null` (the renderer extends it to the last remaining bar, which is
 * now bar k). A model with no candlestick series is returned unchanged.
 */
export function truncateChart(model: ChartModel, k: number): ChartModel {
  const candles = candleSeries(model)
  if (!candles || candles.points.length === 0) return model
  const idx = Math.min(candles.points.length - 1, Math.max(0, Math.floor(k)))
  const t = candles.points[idx].time
  const keep = (p: CandlePoint | ValuePoint): boolean => p.time <= t
  const out: ChartModel = {
    panes: [...model.panes],
    series: model.series.map((s) => ({ ...s, points: s.points.filter(keep) })),
    markers: {},
    levels: {},
    zones: {},
    circles: {},
  }
  for (const [id, list] of Object.entries(model.markers)) out.markers[id] = list.filter((m) => m.time <= t)
  for (const [id, list] of Object.entries(model.levels)) {
    out.levels[id] = list
      .filter((l) => l.time_start <= t)
      .map((l) => (l.time_end !== null && l.time_end > t ? { ...l, time_end: t } : { ...l }))
  }
  for (const [id, list] of Object.entries(model.zones)) {
    out.zones[id] = list.filter((z) => z.time_start <= t).map((z) => (z.time_end > t ? { ...z, time_end: t } : { ...z }))
  }
  for (const [id, list] of Object.entries(model.circles)) {
    out.circles[id] = list.filter((c) => c.time_start <= t).map((c) => (c.time_end > t ? { ...c, time_end: t } : { ...c }))
  }
  return out
}
